import { ReportingPeriod } from "@/types/ReportingPeriod";
import { CheckCircle, Circle, Lock, Unlock } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment-timezone";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

interface PeriodNodeProps {
  period: ReportingPeriod & { daysSincePrevious: number };
  isSelected: boolean;
  index: number;
  onSelect: (period: ReportingPeriod) => void;
  toggleStatus: (period: ReportingPeriod) => void;
}

export function PeriodNode({
  period,
  isSelected,
  index,
  onSelect,
  toggleStatus,
}: PeriodNodeProps) {
  const isComplete = period.isOpen === false;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            id={`period-${period.reportingPeriodId}`}
            className={cn(
              "flex flex-col items-center cursor-pointer relative z-10 group",
              "transform transition-all duration-200 ease-out",
              isSelected ? "scale-110" : "hover:scale-105"
            )}
            onClick={() => onSelect(period)}
          >
            {/* Period node */}
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center",
                "transition-all duration-200 ease-out",
                "border-2 shadow-sm group-hover:shadow-md",
                isComplete
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : "bg-background border-orange-400 text-orange-600",
                isSelected && "ring-2 ring-primary ring-offset-2",
                "group-hover:scale-105"
              )}
            >
              {isComplete ? (
                <CheckCircle  />
              ) : (
                <Circle className="h-5 w-5" />
              )}
            </div>

            {/* Period date */}
            <div
              className={cn(
                "mt-3 px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap",
                "transition-all duration-200",
                isSelected
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground group-hover:bg-muted/50"
              )}
            >
              {moment(period.period).format("MMM Do, YYYY")}
            </div>

            {/* Status indicator */}
            <div
              className={cn(
                "mt-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1.5",
                "transition-all duration-200",
                isComplete
                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  : "bg-orange-100 text-orange-700 border border-orange-200"
              )}
            >
              {isComplete ? (
                <>
                  <Lock className="h-3 w-3" />
                  Closed
                </>
              ) : (
                <>
                  <Unlock className="h-3 w-3" />
                  Open
                </>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            <div className="font-semibold text-sm">
              {moment(period.period).format("MMMM Do, YYYY")}
            </div>
            
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">Status:</span>
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-medium",
                isComplete 
                  ? "bg-emerald-100 text-emerald-700" 
                  : "bg-orange-100 text-orange-700"
              )}>
                {isComplete ? "Closed" : "Open"}
              </span>
            </div>
            
            {index > 0 && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">{period.daysSincePrevious}</span> days since previous period
              </div>
            )}
            
            {index === 0 && (
              <div className="text-xs text-muted-foreground italic">
                First reporting period
              </div>
            )}

            {/* Toggle status button */}
            {!isComplete && 
            <Button
              variant={isComplete ? "outline" : "destructive"}
              size="sm"
              className="w-full mt-3 text-xs gap-1.5"
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus({ ...period, isOpen: !period.isOpen });
              }}
            >
              {isComplete ? (
                <div className="flex items-center gap-1 text-black">
                  {/* <Unlock className="h-3 w-3 " />
                  Reopen Period */}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Close Period
                </div>
              )}
            </Button>
}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
